import { checkIsCallTask, checkReservationExists, checkWorker, getReservationsByTask } from "../../ActionUtils";
import { Task, TransferOptions } from "~/packages/taskrouter";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action } from "~/modules/actions/Action";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface TransferTaskOptions extends TransferOptions {}


































export class StartVoiceTaskTransfer implements Action<Promise<Task>> {
    


    readonly #taskSid: string;

    


    readonly #to: string;

    


    readonly #options?: TransferTaskOptions;

    constructor(taskSid: string, to: string, options?: TransferTaskOptions) {
        this.#taskSid = taskSid;
        this.#to = to;
        this.#options = options;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);

        logger.debug(`StartVoiceTaskTransfer invoked with taskSid: ${this.#taskSid} and to: ${this.#to}`);

        const transferOptions = this.#options;

        const checkCommons = {
            actionName: "StartVoiceTaskTransfer",
            moduleName: "actions/Voice",
            analytics,
            context,
            worker,
            logger
        };

        checkWorker(worker, {
            ...checkCommons,
            event: EVENTS.StartVoiceTaskTransferCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        checkReservationExists(reservation, this.#taskSid, {
            ...checkCommons,
            event: EVENTS.StartVoiceTaskTransferCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        checkIsCallTask(reservation?.task, {
            ...checkCommons,
            event: EVENTS.StartVoiceTaskTransferCompleted,
            extra: {
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation?.sid
            }
        });

        if (reservation?.task.transfers.outgoing?.status === "INITIATED") {
            const errorMsg = `StartVoiceTaskTransfer: Transfer for voice task ${
                this.#taskSid
            } has already been initiated`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.VoiceTaskTransferAlreadyInitiated,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        if (reservation?.workerSid !== worker.sid) {
            const errorMsg = `StartVoiceTaskTransfer: Reservation of the voice task ${
                this.#taskSid
            } is assigned to another worker and cannot be transferred`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation?.sid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.WorkerCannotTransferVoiceTaskFromDifferentWorker,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        try {
            if (transferOptions?.mode === "COLD") {
                await reservation.updateParticipant({ endConferenceOnExit: false });
            }
            const task = await reservation?.task.transfer(this.#to, transferOptions!);

            sendWorkerTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });

            return task;
        } catch (error) {
            if (transferOptions?.mode === "COLD") {
                await reservation.updateParticipant({ endConferenceOnExit: true });
            }
            const errorMsg = `StartVoiceTaskTransfer: Could not transfer voice task ${this.#taskSid}`;
            sendWorkerTrackingEvent(EVENTS.StartVoiceTaskTransferCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                toSid: this.#to,
                mode: transferOptions?.mode,
                reservationSid: reservation.sid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToStartVoiceTaskTransfer,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
